import type { FC, ReactNode } from 'react'
import type { CourseDetail } from '@/types'

import Link from 'next/link'
import {
    CalendarDays,
    ChevronRight,
    Clock,
    MonitorSmartphone
} from 'lucide-react'
import { CmsImage } from '@/components/generals/cms-image'
import { Container } from '@/components/generals/container'
import { WindowFrame } from '@/components/generals/window-frame'
import { LevelBadge } from '@/components/courses/level-badge'

interface CourseHeroProps {
    course: CourseDetail
}

interface MetaItemProps {
    icon: typeof Clock
    label: string
    children: ReactNode
}

const MetaItem: FC<MetaItemProps> = ({ icon: Icon, label, children }): ReactNode => {
    return (
        <div className='rounded-2xl border bg-card p-4'>
            <dt className='flex items-center gap-2 font-mono text-[11px] font-medium tracking-wider text-muted-foreground uppercase'>
                <Icon className='size-3.5 text-primary' aria-hidden='true' />
                {label}
            </dt>

            <dd className='mt-1.5 text-sm leading-snug font-semibold'>
                {children}
            </dd>
        </div>
    )
}

export const CourseHero: FC<CourseHeroProps> = ({ course }): ReactNode => {
    const { instructor } = course

    return (
        <section id='course-overview' aria-labelledby='course-title' className='relative overflow-hidden border-b bg-secondary/40'>
            <div
                aria-hidden='true'
                className='bg-dots pointer-events-none absolute inset-0 text-foreground/40 [mask-image:radial-gradient(ellipse_70%_80%_at_0%_0%,black,transparent)]'
            />

            <Container className='relative grid items-center gap-10 py-10 sm:py-14 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] lg:gap-14 lg:py-16'>
                <div>
                    <nav aria-label="Breadcrumb">
                        <ol className='flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground'>
                            <li>
                                <Link href='/' className='transition-colors hover:text-foreground'>
                                    Home
                                </Link>
                            </li>

                            <li aria-hidden='true'>
                                <ChevronRight className='size-3.5' />
                            </li>

                            <li>
                                <Link href='/courses' className='transition-colors hover:text-foreground'>
                                    Courses
                                </Link>
                            </li>

                            <li aria-hidden='true'>
                                <ChevronRight className='size-3.5' />
                            </li>

                            <li aria-current='page' className='font-medium text-foreground'>
                                <span data-mdlfy-cms={`courses:${course.id}:title`}>{course.title}</span>
                            </li>
                        </ol>
                    </nav>

                    <div className='mt-7 flex flex-wrap items-center gap-2'>
                        <span className='rounded-full border bg-card px-3 py-1 font-mono text-[11px] font-medium tracking-wider text-primary uppercase'>
                            <span data-mdlfy-cms={`courses:${course.id}:category`}>{course.category}</span>
                        </span>

                        <LevelBadge courseId={course.id} level={course.level} />
                    </div>

                    <h1 id='course-title' className='mt-4 text-4xl leading-[1.05] font-semibold sm:text-5xl lg:text-[3.5rem]'>
                        <span data-mdlfy-cms={`courses:${course.id}:title`}>{course.title}</span>
                    </h1>

                    <p className='mt-5 max-w-2xl text-lg leading-relaxed text-muted-foreground' data-mdlfy-cms={`courses:${course.id}:summary`}>
                        {course.summary}
                    </p>

                    {(course.duration || course.schedule || course.format) && (
                        <dl className='mt-8 grid gap-3 sm:grid-cols-3'>
                            {course.duration && (
                                <MetaItem icon={Clock} label="Duration">
                                    <span data-mdlfy-cms={`courses:${course.id}:duration`}>{course.duration}</span>
                                </MetaItem>
                            )}

                            {course.schedule && (
                                <MetaItem icon={CalendarDays} label="Schedule">
                                    <span data-mdlfy-cms={`courses:${course.id}:schedule`}>{course.schedule}</span>
                                </MetaItem>
                            )}

                            {course.format && (
                                <MetaItem icon={MonitorSmartphone} label="Format">
                                    <span data-mdlfy-cms={`courses:${course.id}:format`}>{course.format}</span>
                                </MetaItem>
                            )}
                        </dl>
                    )}

                    {instructor && (
                        <div className='mt-8 flex items-center gap-3'>
                            <span className='relative size-11 shrink-0 overflow-hidden rounded-full border-2 border-card bg-secondary shadow-sm'>
                                <CmsImage src={instructor.photo} alt={null} sizes='44px' className='object-[center_20%]' />
                            </span>

                            <p className='text-sm'>
                                <span className='text-muted-foreground'>Taught by </span>
                                <a href='#instructor' className='font-semibold transition-colors hover:text-primary'>
                                    <span data-mdlfy-cms={`instructors:${instructor.id}:name`}>{instructor.name}</span>
                                </a>
                            </p>
                        </div>
                    )}
                </div>

                <WindowFrame title={`${course.slug}.course`} className='w-full'>
                    <div className='relative aspect-[4/3]'>
                        <CmsImage
                            src={course.image}
                            alt={course.image_alt}
                            sizes='(min-width: 1280px) 560px, (min-width: 1024px) 45vw, 100vw'
                            preload
                        />
                    </div>
                </WindowFrame>
            </Container>
        </section>
    )
}
